import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useElapsedSeconds, useUsers, userMap } from "@/lib/hooks";
import { fileToDataUrl } from "@/lib/files";
import { fmtClock, fmtDuration, safeUrl } from "@/lib/utils";
import { PageHeader } from "@/components/PageHeader";
import { TaskModal } from "@/components/TaskModal";
import { UserAvatar } from "@/components/UserAvatar";
import { ClientContacts, normalizeClientContacts } from "@/components/ClientContacts";
import { LogoBgField } from "@/components/LogoBgField";
import { SERVICES, ROLE_LABELS, CRED_CATEGORIES, PRIORITY, TASK_STATUS, eurFmt, hasFinancialAccess } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, Copy, Eye, EyeOff, Lock, Globe, Plus, Trash2, Phone,
  Palette, Type, FileText, Save, KeyRound, ShieldAlert, Timer,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";

function copy(text, label = "Copiado") {
  navigator.clipboard.writeText(text);
  toast.success(label);
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: users = [] } = useUsers();
  const um = useMemo(() => userMap(users), [users]);

  const { data: client, isLoading } = useQuery({
    queryKey: ["client", id],
    queryFn: async () => (await api.get(`/clients/${id}`)).data,
  });

  if (isLoading || !client) return <p className="text-muted-foreground text-sm">A carregar...</p>;

  const isCeo = ["ADMIN", "SUB_ADMIN"].includes(user.role);
  const canSeeFinancials = hasFinancialAccess(user);
  const deleteClient = async () => {
    if (!window.confirm(`Eliminar o cliente "${client.name}"? Isto apaga também chamadas, propostas, credenciais e documentos associados.`)) return;
    try {
      await api.delete(`/clients/${client.id}`);
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Cliente eliminado");
      navigate("/clientes");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <button onClick={() => navigate("/clientes")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-clients">
        <ArrowLeft className="h-4 w-4" /> Base de Clientes
      </button>

      <div className="flex items-center gap-4 mb-6">
        <div
          className="h-16 w-16 rounded-xl bg-muted flex items-center justify-center overflow-hidden shrink-0"
          style={client.logoBg ? { backgroundColor: client.logoBg } : undefined}
        >
          {safeUrl(client.logoUrl) ? <img src={safeUrl(client.logoUrl)} alt={client.name} className="h-full w-full object-contain" /> : <Building2 className="h-7 w-7 text-muted-foreground" />}
        </div>
        <div>
          <h1 className="font-poppins font-bold text-2xl lg:text-3xl">{client.name}</h1>
          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5">
            {client.nif && <span>NIF {client.nif}</span>}
            <Badge className={`rounded-full text-[10px] ${client.status === "ATIVO" ? "bg-primary/15 text-primary" : "bg-muted"}`}>{client.status === "ATIVO" ? "Ativo" : "Inativo"}</Badge>
          </div>
        </div>
        {isCeo && (
          <Button
            variant="ghost"
            size="sm"
            onClick={deleteClient}
            className="ml-auto text-destructive hover:text-destructive hover:bg-destructive/10"
            data-testid="delete-client"
          >
            <Trash2 className="h-4 w-4 mr-1.5" /> Eliminar cliente
          </Button>
        )}
      </div>

      <Tabs defaultValue="perfil">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="perfil" data-testid="tab-perfil">Perfil 360º</TabsTrigger>
          <TabsTrigger value="marca" data-testid="tab-marca">Identidade Visual</TabsTrigger>
          <TabsTrigger value="cofre" data-testid="tab-cofre">Cofre</TabsTrigger>
          <TabsTrigger value="chamadas" data-testid="tab-chamadas">Chamadas</TabsTrigger>
          <TabsTrigger value="tarefas" data-testid="tab-tarefas">Tarefas</TabsTrigger>
          <TabsTrigger value="tempo" data-testid="tab-tempo">Tempo</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos">Documentos</TabsTrigger>
          {canSeeFinancials && <TabsTrigger value="propostas" data-testid="tab-propostas">Propostas</TabsTrigger>}
        </TabsList>

        <TabsContent value="perfil"><PerfilTab client={client} users={users} user={user} qc={qc} /></TabsContent>
        <TabsContent value="marca"><MarcaTab client={client} qc={qc} /></TabsContent>
        <TabsContent value="cofre"><CofreTab clientId={id} /></TabsContent>
        <TabsContent value="chamadas"><ChamadasTab clientId={id} um={um} /></TabsContent>
        <TabsContent value="tarefas"><TarefasTab clientId={id} um={um} /></TabsContent>
        <TabsContent value="tempo"><TempoTab clientId={id} /></TabsContent>
        <TabsContent value="documentos"><DocumentosTab clientId={id} /></TabsContent>
        {canSeeFinancials && <TabsContent value="propostas"><PropostasTab clientId={id} /></TabsContent>}
      </Tabs>
    </div>
  );
}

/* ---------------- Perfil ---------------- */
function PerfilTab({ client, users, user, qc }) {
  const canSeeFinancials = hasFinancialAccess(user);
  const [form, setForm] = useState({ ...client, contacts: normalizeClientContacts(client.contacts) });
  const [saving, setSaving] = useState(false);
  const toggleService = (s) =>
    setForm((f) => ({ ...f, services: (f.services || []).includes(s) ? f.services.filter((x) => x !== s) : [...(f.services || []), s] }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, associateIds: form.associateIds || [] };
      if (canSeeFinancials) payload.retainerValue = parseFloat(form.retainerValue) || 0;
      else delete payload.retainerValue;
      await api.put(`/clients/${client.id}`, payload);
      qc.invalidateQueries({ queryKey: ["client", client.id] });
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Cliente atualizado");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await fileToDataUrl(file);
    setForm((f) => ({ ...f, logoUrl: url }));
  };
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <Card className="p-6 lg:col-span-2 space-y-4">
        <h3 className="font-poppins font-semibold">Dados da empresa</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
          <div><Label>NIF</Label><Input value={form.nif || ""} onChange={(e) => setForm({ ...form, nif: e.target.value })} className="mt-1" /></div>
          <div className="sm:col-span-2"><ClientContacts contacts={form.contacts} onChange={(contacts) => setForm({ ...form, contacts })} /></div>
          {canSeeFinancials && <div><Label>Avença mensal (€)</Label><Input type="number" value={form.retainerValue || ""} onChange={(e) => setForm({ ...form, retainerValue: e.target.value })} className="mt-1" /></div>}
          <div><Label>Estado</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ATIVO">Ativo</SelectItem><SelectItem value="INATIVO">Inativo</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Associados</Label>
          <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">Membros da equipa que trabalham com este cliente.</p>
          <div className="flex flex-wrap gap-2">
            {users.map((u) => {
              const active = (form.associateIds || []).includes(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, associateIds: active ? (f.associateIds || []).filter((x) => x !== u.id) : [...(f.associateIds || []), u.id] }))}
                  className={`flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border text-xs transition-colors ${active ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:border-primary/50"}`}
                >
                  <UserAvatar user={u} size="h-5 w-5" />
                  {u.name.split(" ")[0]}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <Label>Serviços</Label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {SERVICES.map((s) => (
              <button key={s} type="button" onClick={() => toggleService(s)}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${(form.services || []).includes(s) ? "bg-primary text-black border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-primary text-black hover:bg-primary/90" data-testid="save-client-profile"><Save className="h-4 w-4 mr-1.5" />{saving ? "A guardar..." : "Guardar"}</Button>
      </Card>
      <Card className="p-6">
        <h3 className="font-poppins font-semibold mb-4">Logótipo</h3>
        <div
          className="h-32 rounded-lg bg-muted flex items-center justify-center overflow-hidden mb-3"
          style={form.logoBg ? { backgroundColor: form.logoBg } : undefined}
        >
          {safeUrl(form.logoUrl) ? <img src={safeUrl(form.logoUrl)} className="h-full w-full object-contain p-3" alt="" /> : <Building2 className="h-8 w-8 text-muted-foreground" />}
        </div>
        <Input type="file" accept="image/*" onChange={onLogo} className="text-sm" />
        <div className="mt-3">
          <LogoBgField value={form.logoBg || ""} onChange={(v) => setForm((f) => ({ ...f, logoBg: v }))} />
        </div>
      </Card>
    </div>
  );
}

/* ---------------- Marca / Brand Kit ---------------- */
function MarcaTab({ client, qc }) {
  const [palette, setPalette] = useState(client.colorPalette || []);
  const [fonts, setFonts] = useState(client.typography || []);
  const [tone, setTone] = useState(client.toneOfVoice || "");
  const [audience, setAudience] = useState(client.targetAudience || "");
  const [logos, setLogos] = useState(client.logos || []);
  const [pdf, setPdf] = useState(client.guidelinesPdf || null);
  const [newHex, setNewHex] = useState("#A886CD");
  const [newHexName, setNewHexName] = useState("");
  const [newFont, setNewFont] = useState({ name: "", link: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}`, {
        ...client, colorPalette: palette, typography: fonts, toneOfVoice: tone, targetAudience: audience, logos, guidelinesPdf: pdf,
        retainerValue: parseFloat(client.retainerValue) || 0,
      });
      qc.invalidateQueries({ queryKey: ["client", client.id] });
      toast.success("Identidade visual guardada");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addLogo = async (e) => {
    const files = Array.from(e.target.files || []);
    const added = [];
    for (const f of files) {
      added.push({ id: crypto.randomUUID(), name: f.name, format: f.name.split(".").pop().toUpperCase(), dataUrl: await fileToDataUrl(f) });
    }
    setLogos((l) => [...l, ...added]);
  };
  const addPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPdf({ id: crypto.randomUUID(), name: f.name, format: "PDF", dataUrl: await fileToDataUrl(f) });
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-primary text-black hover:bg-primary/90" data-testid="save-brand"><Save className="h-4 w-4 mr-1.5" />{saving ? "A guardar..." : "Guardar Kit"}</Button>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4"><FileText className="h-4 w-4 text-primary" /><h3 className="font-poppins font-semibold">Ficheiros de logótipo</h3></div>
        <div className="flex flex-wrap gap-3 mb-3">
          {logos.map((lg) => (
            <div key={lg.id} className="relative group w-28">
              <div className="h-20 rounded-lg border border-border bg-muted flex items-center justify-center overflow-hidden">
                <img src={safeUrl(lg.dataUrl, "#")} className="h-full w-full object-contain p-2" alt={lg.name} />
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-muted-foreground truncate">{lg.format}</span>
                <button onClick={() => setLogos(logos.filter((x) => x.id !== lg.id))} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>
        <Input type="file" accept="image/*" multiple onChange={addLogo} className="text-sm max-w-xs" data-testid="logo-upload" />
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4"><Palette className="h-4 w-4 text-primary" /><h3 className="font-poppins font-semibold">Paleta de cores</h3></div>
        <div className="flex flex-wrap gap-3 mb-4">
          {palette.map((c, i) => (
            <div key={i} className="group">
              <div className="h-16 w-16 rounded-lg border border-border cursor-pointer relative" style={{ backgroundColor: c.hex }} onClick={() => copy(c.hex, `${c.hex} copiado`)}>
                <button onClick={(e) => { e.stopPropagation(); setPalette(palette.filter((_, j) => j !== i)); }} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-card border border-border items-center justify-center hidden group-hover:flex"><Trash2 className="h-3 w-3 text-destructive" /></button>
              </div>
              <div className="flex items-center gap-1 mt-1 justify-center">
                <span className="text-[11px] font-mono">{c.hex}</span>
                <Copy className="h-3 w-3 text-muted-foreground cursor-pointer" onClick={() => copy(c.hex, `${c.hex} copiado`)} />
              </div>
              {c.name && <div className="text-[10px] text-muted-foreground text-center">{c.name}</div>}
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <input type="color" value={newHex} onChange={(e) => setNewHex(e.target.value)} className="h-10 w-12 rounded border border-border cursor-pointer" />
          <Input value={newHexName} onChange={(e) => setNewHexName(e.target.value)} placeholder="Nome (opcional)" className="max-w-[160px]" />
          <Button variant="outline" onClick={() => { setPalette([...palette, { hex: newHex.toUpperCase(), name: newHexName }]); setNewHexName(""); }} data-testid="add-color"><Plus className="h-4 w-4 mr-1" />Adicionar</Button>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4"><Type className="h-4 w-4 text-primary" /><h3 className="font-poppins font-semibold">Tipografia</h3></div>
        <div className="space-y-2 mb-4">
          {fonts.map((f, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div><span className="font-poppins font-medium text-sm">{f.name}</span>{safeUrl(f.link) && <a href={safeUrl(f.link)} target="_blank" rel="noreferrer" className="text-xs text-primary ml-2 underline">download</a>}</div>
              <button onClick={() => setFonts(fonts.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <Input value={newFont.name} onChange={(e) => setNewFont({ ...newFont, name: e.target.value })} placeholder="Nome da fonte" className="max-w-[200px]" />
          <Input value={newFont.link} onChange={(e) => setNewFont({ ...newFont, link: e.target.value })} placeholder="Link de download" className="max-w-[240px]" />
          <Button variant="outline" onClick={() => { if (newFont.name) { setFonts([...fonts, newFont]); setNewFont({ name: "", link: "" }); } }}><Plus className="h-4 w-4 mr-1" />Adicionar</Button>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-5">
        <Card className="p-6"><h3 className="font-poppins font-semibold mb-3">Tone of Voice</h3><Textarea value={tone} onChange={(e) => setTone(e.target.value)} rows={4} placeholder="Descreva o tom de comunicação..." /></Card>
        <Card className="p-6"><h3 className="font-poppins font-semibold mb-3">Público-alvo / Persona</h3><Textarea value={audience} onChange={(e) => setAudience(e.target.value)} rows={4} placeholder="Descreva o público-alvo..." /></Card>
      </div>

      <Card className="p-6">
        <h3 className="font-poppins font-semibold mb-3">Manual de Normas (PDF)</h3>
        {pdf ? (
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-primary" />
            <a href={safeUrl(pdf.dataUrl, "#")} download={pdf.name} className="text-sm text-primary underline">{pdf.name}</a>
            <button onClick={() => setPdf(null)} className="text-muted-foreground hover:text-destructive ml-2"><Trash2 className="h-4 w-4" /></button>
          </div>
        ) : (
          <Input type="file" accept="application/pdf" onChange={addPdf} className="text-sm max-w-xs" />
        )}
      </Card>
    </div>
  );
}

/* ---------------- Cofre ---------------- */
function CofreTab({ clientId }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["credentials", clientId], queryFn: async () => (await api.get(`/clients/${clientId}/credentials`)).data });
  const creds = data?.credentials || [];
  const canManage = Boolean(data?.canManage);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [form, setForm] = useState({ category: CRED_CATEGORIES[0], label: "", username: "", password: "", url: "", notes: "", access: "RESTRITO" });

  const openNew = () => { setEditing(null); setForm({ category: CRED_CATEGORIES[0], label: "", username: "", password: "", url: "", notes: "", access: "RESTRITO" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c, password: "" }); setOpen(true); };

  const save = async () => {
    if (!form.label.trim()) return toast.error("Indique um rótulo");
    try {
      if (editing?.id) await api.put(`/credentials/${editing.id}`, form);
      else await api.post(`/clients/${clientId}/credentials`, form);
      qc.invalidateQueries({ queryKey: ["credentials", clientId] });
      toast.success("Credencial guardada"); setOpen(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/credentials/${id}`); qc.invalidateQueries({ queryKey: ["credentials", clientId] }); toast.success("Eliminada"); };
  const reveal = async (c) => {
    if (revealed[c.id]) { setRevealed((r) => ({ ...r, [c.id]: undefined })); return; }
    try {
      const { data } = await api.get(`/credentials/${c.id}/reveal`);
      setRevealed((r) => ({ ...r, [c.id]: data.password }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const byCat = creds.reduce((acc, c) => { (acc[c.category] = acc[c.category] || []).push(c); return acc; }, {});

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-primary" /> Credenciais encriptadas (AES-256). Acessos são auditados.</p>
        {canManage && <Button onClick={openNew} className="bg-primary text-black hover:bg-primary/90" data-testid="add-credential"><Plus className="h-4 w-4 mr-1.5" />Nova Credencial</Button>}
      </div>
      {creds.length === 0 && <Card className="p-10 text-center border-dashed"><KeyRound className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" /><p className="text-sm text-muted-foreground">Sem credenciais guardadas.</p></Card>}
      <div className="space-y-5">
        {Object.entries(byCat).map(([cat, items]) => (
          <div key={cat}>
            <h4 className="font-poppins font-semibold text-xs uppercase tracking-wide text-muted-foreground mb-2">{cat}</h4>
            <div className="space-y-2">
              {items.map((c) => (
                <Card key={c.id} className="p-4" data-testid={`cred-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-poppins font-medium text-sm">{c.label}</span>
                        <Badge className={`rounded-full text-[10px] ${c.access === "RESTRITO" ? "bg-destructive/15 text-destructive" : "bg-muted text-muted-foreground"}`}>
                          {c.access === "RESTRITO" ? <><Lock className="h-2.5 w-2.5 mr-1" />Restrito</> : <><Globe className="h-2.5 w-2.5 mr-1" />Público</>}
                        </Badge>
                      </div>
                      {c.username && (
                        <div className="flex items-center gap-2 mt-1.5 text-sm">
                          <span className="text-muted-foreground text-xs w-16">Utilizador</span>
                          <span className="font-mono text-xs">{c.username}</span>
                          <Copy className="h-3 w-3 text-muted-foreground cursor-pointer" onClick={() => copy(c.username)} />
                        </div>
                      )}
                      {c.hasPassword && (
                        <div className="flex items-center gap-2 mt-1 text-sm">
                          <span className="text-muted-foreground text-xs w-16">Password</span>
                          <span className="font-mono text-xs">{revealed[c.id] ?? "••••••••••"}</span>
                          <button onClick={() => reveal(c)} className="text-muted-foreground hover:text-foreground" data-testid={`reveal-${c.id}`}>{revealed[c.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
                          {revealed[c.id] && <Copy className="h-3 w-3 text-muted-foreground cursor-pointer" onClick={() => copy(revealed[c.id])} />}
                        </div>
                      )}
                      {safeUrl(c.url) && <a href={safeUrl(c.url)} target="_blank" rel="noreferrer" className="text-xs text-primary underline mt-1 inline-block">{c.url}</a>}
                      {c.notes && <p className="text-xs text-muted-foreground mt-1">{c.notes}</p>}
                    </div>
                    {canManage && (
                      <div className="flex gap-1">
                        <button onClick={() => openEdit(c)} className="text-muted-foreground hover:text-foreground text-xs">Editar</button>
                        <button onClick={() => del(c.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="credential-modal">
          <DialogHeader><DialogTitle className="font-poppins">{editing ? "Editar Credencial" : "Nova Credencial"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CRED_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Rótulo</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1" data-testid="cred-label" placeholder="Ex.: Conta principal" /></div>
            <div><Label>Utilizador / Email</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1" /></div>
            <div><Label>Password {editing && <span className="text-xs text-muted-foreground">(deixe vazio para manter)</span>}</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 font-mono" data-testid="cred-password" /></div>
            <div><Label>URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="mt-1" /></div>
            <div><Label>Notas</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" /></div>
            <div><Label>Nível de acesso</Label>
              <Select value={form.access} onValueChange={(v) => setForm({ ...form, access: v })}>
                <SelectTrigger className="mt-1" data-testid="cred-access"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="PUBLICO">Público (toda a equipa)</SelectItem><SelectItem value="RESTRITO">Restrito (gestores/admins)</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-primary text-black hover:bg-primary/90" data-testid="cred-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Chamadas ---------------- */
const emptyCall = () => ({ date: new Date().toISOString().slice(0, 10), time: "", summary: "", createTask: false, taskTitle: "", taskDue: "" });

function ChamadasTab({ clientId }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: calls = [] } = useQuery({ queryKey: ["calls", clientId], queryFn: async () => (await api.get(`/clients/${clientId}/calls`)).data });
  const [form, setForm] = useState(emptyCall());
  const [saving, setSaving] = useState(false);
  const [taskFor, setTaskFor] = useState(null); // chamada -> abrir TaskModal
  const taskSeed = useMemo(
    () => (taskFor ? { clientId, description: `Seguimento da chamada de ${taskFor.date}:\n${taskFor.summary}` } : null),
    [taskFor, clientId],
  );

  const add = async () => {
    if (!form.summary.trim()) return toast.error("Descreva a chamada");
    if (form.createTask && !form.taskTitle.trim()) return toast.error("Dê um título à tarefa de seguimento");
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/calls`, { date: form.date, time: form.time, summary: form.summary });
      if (form.createTask) {
        await api.post("/tasks", {
          title: form.taskTitle.trim(),
          description: `Seguimento da chamada de ${form.date}:\n${form.summary}`,
          clientId,
          // Fica com quem registou a chamada: sem responsável, ninguém a via.
          assigneeIds: [user.id],
          dueDate: form.taskDue || "",
          priority: "MEDIA",
          status: "A_FAZER",
        });
        qc.invalidateQueries({ queryKey: ["tasks"] });
      }
      qc.invalidateQueries({ queryKey: ["calls", clientId] });
      setForm(emptyCall());
      toast.success(form.createTask ? "Chamada registada + tarefa criada" : "Chamada registada");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/calls/${id}`); qc.invalidateQueries({ queryKey: ["calls", clientId] }); };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <Card className="p-5 space-y-3">
        <h3 className="font-poppins font-semibold flex items-center gap-2"><Phone className="h-4 w-4 text-primary" />Registar chamada</h3>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Hora</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1" /></div>
        </div>
        <div><Label className="text-xs">Resumo</Label><Textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={3} className="mt-1" data-testid="call-summary" /></div>

        <div className="rounded-lg border border-border p-3 space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input type="checkbox" checked={form.createTask} onChange={(e) => setForm({ ...form, createTask: e.target.checked })} className="accent-primary" />
            Criar tarefa de seguimento
          </label>
          {form.createTask && (
            <div className="space-y-2">
              <Input value={form.taskTitle} onChange={(e) => setForm({ ...form, taskTitle: e.target.value })} placeholder="O que é preciso fazer?" className="text-sm" data-testid="call-task-title" />
              <div><Label className="text-xs">Prazo (opcional)</Label><Input type="date" value={form.taskDue} onChange={(e) => setForm({ ...form, taskDue: e.target.value })} className="mt-1" /></div>
            </div>
          )}
        </div>

        <Button onClick={add} disabled={saving} className="w-full bg-primary text-black hover:bg-primary/90" data-testid="call-save">{saving ? "A guardar..." : "Registar"}</Button>
      </Card>

      <div className="lg:col-span-2 space-y-2">
        {calls.length === 0 && <p className="text-sm text-muted-foreground">Sem registos de chamadas.</p>}
        {calls.map((c) => (
          <Card key={c.id} className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="font-poppins font-medium text-sm">{c.date}{c.time && ` · ${c.time}`}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{c.createdBy}</span>
                <button onClick={() => del(c.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <p className="text-sm font-roboto">{c.summary}</p>
            {c.nextActions && <p className="text-xs text-primary mt-1.5">➜ {c.nextActions}</p>}
            <button onClick={() => setTaskFor(c)} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              <Plus className="h-3 w-3" /> Criar tarefa
            </button>
          </Card>
        ))}
      </div>

      <TaskModal
        open={Boolean(taskFor)}
        onOpenChange={(v) => !v && setTaskFor(null)}
        task={taskSeed}
        fixedClientId={clientId}
        onSaved={() => { setTaskFor(null); qc.invalidateQueries({ queryKey: ["tasks"] }); }}
      />
    </div>
  );
}

/* ---------------- Documentos ---------------- */
const DOC_CATEGORIES = [
  { value: "COMPROVATIVO", label: "Comprovativo de pagamento" },
  { value: "RECIBO", label: "Recibo" },
  { value: "FATURA", label: "Fatura" },
  { value: "CONTRATO", label: "Contrato" },
  { value: "OUTRO", label: "Outro" },
];

function DocumentosTab({ clientId }) {
  const qc = useQueryClient();
  const { data: docs = [] } = useQuery({ queryKey: ["documents", clientId], queryFn: async () => (await api.get(`/clients/${clientId}/documents`)).data });
  const [form, setForm] = useState({ name: "", category: "COMPROVATIVO", date: new Date().toISOString().slice(0, 10), amount: "", dataUrl: "", fileName: "" });
  const [saving, setSaving] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    setForm((f) => ({ ...f, dataUrl, fileName: file.name, name: f.name || file.name.replace(/\.[^.]+$/, "") }));
  };
  const add = async () => {
    if (!form.dataUrl) return toast.error("Escolha um ficheiro (PDF ou imagem)");
    if (!form.name.trim()) return toast.error("Dê um nome ao documento");
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/documents`, {
        name: form.name.trim(),
        category: form.category,
        date: form.date,
        amount: parseFloat(form.amount) || 0,
        dataUrl: form.dataUrl,
      });
      qc.invalidateQueries({ queryKey: ["documents", clientId] });
      setForm({ name: "", category: "COMPROVATIVO", date: new Date().toISOString().slice(0, 10), amount: "", dataUrl: "", fileName: "" });
      toast.success("Documento guardado");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminar este documento?")) return;
    await api.delete(`/documents/${id}`);
    qc.invalidateQueries({ queryKey: ["documents", clientId] });
  };
  const catLabel = (v) => DOC_CATEGORIES.find((c) => c.value === v)?.label || v;

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <Card className="p-5 space-y-3">
        <h3 className="font-poppins font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-primary" />Novo documento</h3>
        <div><Label className="text-xs">Ficheiro (PDF ou imagem)</Label><Input type="file" accept="image/*,application/pdf" onChange={onFile} className="mt-1 text-sm" data-testid="doc-file" />{form.fileName && <p className="mt-1 text-xs text-muted-foreground truncate">{form.fileName}</p>}</div>
        <div><Label className="text-xs">Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Ex.: Pagamento avença março" /></div>
        <div><Label className="text-xs">Categoria</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Valor (€)</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" placeholder="0,00" /></div>
        </div>
        <Button onClick={add} disabled={saving} className="w-full bg-primary text-black hover:bg-primary/90" data-testid="doc-save">{saving ? "A guardar..." : "Guardar documento"}</Button>
      </Card>

      <div className="lg:col-span-2 space-y-2">
        {docs.length === 0 && <p className="text-sm text-muted-foreground">Ainda não há documentos.</p>}
        {docs.map((d) => (
          <Card key={d.id} className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-primary" /></div>
            <div className="min-w-0 flex-1">
              <div className="font-poppins font-medium text-sm truncate">{d.name}</div>
              <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2">
                <span>{catLabel(d.category)}</span>
                {d.date && <span>· {d.date}</span>}
                {d.amount > 0 && <span>· {eurFmt.format(d.amount)}</span>}
                {d.uploadedBy && <span>· {d.uploadedBy}</span>}
              </div>
            </div>
            {safeUrl(d.dataUrl) && (
              <a href={safeUrl(d.dataUrl)} target="_blank" rel="noreferrer" className="text-xs font-medium text-primary hover:underline shrink-0">Abrir</a>
            )}
            <button onClick={() => del(d.id)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Tarefas ---------------- */
function TarefasTab({ clientId, um }) {
  const qc = useQueryClient();
  const { data: tasks = [] } = useQuery({ queryKey: ["tasks", "client", clientId], queryFn: async () => (await api.get("/tasks", { params: { clientId } })).data });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const del = async (id) => { await api.delete(`/tasks/${id}`); qc.invalidateQueries({ queryKey: ["tasks"] }); toast.success("Eliminada"); };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">Tarefas ligadas a este cliente aparecem no To-Do do responsável.</p>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-primary text-black hover:bg-primary/90" data-testid="add-client-task"><Plus className="h-4 w-4 mr-1.5" />Nova Tarefa</Button>
      </div>
      <div className="space-y-2">
        {tasks.length === 0 && <p className="text-sm text-muted-foreground">Sem tarefas.</p>}
        {tasks.map((t) => (
          <Card key={t.id} className="p-4 flex items-center gap-3 cursor-pointer hover:border-primary/40" onClick={() => { setEditing(t); setOpen(true); }}>
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: PRIORITY[t.priority]?.color }} />
            <span className="font-poppins font-medium text-sm flex-1">{t.title}</span>
            <Badge className="rounded-full text-[10px]" style={{ backgroundColor: TASK_STATUS[t.status]?.color, color: "#fff" }}>{TASK_STATUS[t.status]?.label}</Badge>
            <div className="flex -space-x-1.5">{(t.assigneeIds || []).map((id) => um[id] && <UserAvatar key={id} user={um[id]} size="h-6 w-6 ring-1 ring-card" />)}</div>
            <button onClick={(e) => { e.stopPropagation(); del(t.id); }} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
          </Card>
        ))}
      </div>
      <TaskModal open={open} onOpenChange={setOpen} task={editing} fixedClientId={clientId} onSaved={() => qc.invalidateQueries({ queryKey: ["tasks", "client", clientId] })} />
    </div>
  );
}

/* ---------------- Propostas ---------------- */
function PropostasTab({ clientId }) {
  const qc = useQueryClient();
  const { data: props = [] } = useQuery({ queryKey: ["proposals", clientId], queryFn: async () => (await api.get(`/clients/${clientId}/proposals`)).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", value: "", status: "ENVIADA", lossReason: "", isRecurring: false, monthlyValue: "", renewalDate: "", sentDate: new Date().toISOString().slice(0, 10) });
  const STATUS = { ENVIADA: { label: "Enviada", color: "#5B8DEF" }, GANHA: { label: "Ganha", color: "#4C9A8E" }, PERDIDA: { label: "Perdida", color: "#D64545" } };
  const save = async () => {
    if (!form.title.trim()) return toast.error("Indique um título");
    await api.post(`/clients/${clientId}/proposals`, { ...form, value: parseFloat(form.value) || 0, monthlyValue: parseFloat(form.monthlyValue) || 0 });
    qc.invalidateQueries({ queryKey: ["proposals", clientId] });
    setOpen(false); toast.success("Proposta registada");
  };
  const setStatus = async (p, status) => { await api.put(`/proposals/${p.id}`, { ...p, status }); qc.invalidateQueries({ queryKey: ["proposals", clientId] }); };
  const del = async (id) => { await api.delete(`/proposals/${id}`); qc.invalidateQueries({ queryKey: ["proposals", clientId] }); };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setOpen(true)} className="bg-primary text-black hover:bg-primary/90" data-testid="add-proposal"><Plus className="h-4 w-4 mr-1.5" />Nova Proposta</Button>
      </div>
      <div className="space-y-2">
        {props.length === 0 && <p className="text-sm text-muted-foreground">Sem propostas.</p>}
        {props.map((p) => (
          <Card key={p.id} className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2"><span className="font-poppins font-medium text-sm">{p.title}</span>{p.isRecurring && <Badge variant="secondary" className="rounded-full text-[10px]">Recorrente</Badge>}</div>
                <span className="text-xs text-muted-foreground">{eurFmt.format(p.value)}{p.isRecurring && ` · ${eurFmt.format(p.monthlyValue)}/mês`}</span>
                {p.status === "PERDIDA" && p.lossReason && <p className="text-xs text-destructive mt-1">Motivo: {p.lossReason}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Select value={p.status} onValueChange={(v) => setStatus(p, v)}>
                  <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => del(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="proposal-modal">
          <DialogHeader><DialogTitle className="font-poppins">Nova Proposta</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="proposal-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Valor (€)</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1" data-testid="proposal-value" /></div>
              <div><Label>Estado</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ENVIADA">Enviada</SelectItem><SelectItem value="GANHA">Ganha</SelectItem><SelectItem value="PERDIDA">Perdida</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isRecurring} onChange={(e) => setForm({ ...form, isRecurring: e.target.checked })} /> Avença recorrente</label>
            {form.isRecurring && <div className="grid grid-cols-2 gap-3">
              <div><Label>Valor mensal (€)</Label><Input type="number" value={form.monthlyValue} onChange={(e) => setForm({ ...form, monthlyValue: e.target.value })} className="mt-1" /></div>
              <div><Label>Data de renovação</Label><Input type="date" value={form.renewalDate} onChange={(e) => setForm({ ...form, renewalDate: e.target.value })} className="mt-1" data-testid="proposal-renewal" /></div>
            </div>}
            {form.status === "PERDIDA" && <div><Label>Motivo da perda</Label><Input value={form.lossReason} onChange={(e) => setForm({ ...form, lossReason: e.target.value })} className="mt-1" /></div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-primary text-black hover:bg-primary/90" data-testid="proposal-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Tempo (interações) ---------------- */
function LiveInteractionRow({ interaction }) {
  const elapsed = useElapsedSeconds(interaction.startedAt);
  return (
    <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 text-sm">
      <UserAvatar user={{ name: interaction.userName }} size="h-7 w-7" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{interaction.userName}</div>
        <div className="truncate text-[11px] text-muted-foreground">{interaction.taskTitle || interaction.description || "Sem descrição"}</div>
      </div>
      <span className="shrink-0 rounded-full bg-primary/15 px-2.5 py-1 font-mono text-xs tabular-nums text-primary">{fmtClock(elapsed)}</span>
    </div>
  );
}

function TempoTab({ clientId }) {
  const { data: interactions = [] } = useQuery({
    queryKey: ["interactions", clientId],
    queryFn: async () => (await api.get("/interactions", { params: { clientId, limit: 200 } })).data,
    refetchInterval: 30000,
  });
  const live = interactions.filter((i) => i.status === "EM_CURSO");
  const done = interactions.filter((i) => i.status !== "EM_CURSO");
  const totalMinutes = done.reduce((sum, i) => sum + (i.minutes || 0), 0);

  return (
    <div className="space-y-5">
      {live.length > 0 && (
        <Card className="p-5 border-primary/30 bg-primary/5">
          <h3 className="font-poppins font-semibold mb-3 flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
            </span>
            A trabalhar agora
          </h3>
          <div className="space-y-2">
            {live.map((i) => <LiveInteractionRow key={i.id} interaction={i} />)}
          </div>
        </Card>
      )}
      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="font-poppins font-semibold flex items-center gap-2"><Timer className="h-4 w-4 text-primary" />Histórico de interações</h3>
            <p className="text-xs text-muted-foreground">Tempo lançado através do cronómetro de cliente</p>
          </div>
          <span className="font-poppins font-semibold text-primary">{fmtDuration(totalMinutes)}</span>
        </div>
        {done.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ainda sem interações registadas para este cliente.</p>
        ) : (
          <div className="space-y-2">
            {done.map((i) => (
              <div key={i.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 text-sm">
                <UserAvatar user={{ name: i.userName }} size="h-7 w-7" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{i.taskTitle || i.description || "Sem descrição"}</div>
                  <div className="text-[11px] text-muted-foreground">{i.userName} · {format(parseISO(i.startedAt), "d MMM, HH:mm", { locale: pt })}</div>
                </div>
                <span className="shrink-0 text-xs font-medium text-muted-foreground">{fmtDuration(i.minutes)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
